import i18n, { Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';
import es from './locales/es.json';
import ht from './locales/ht.json';

const resources: Resource = {
  en: { translation: en },
  es: { translation: es },
  ht: { translation: ht },
};
/**
 * Language normalization map for handling locale variants
 * Android/iOS devices may report compound language codes that need mapping
 * Example: Android reports 'fr-HT' (French-Haiti) but app expects 'ht' (Haitian Creole)
 */
const languageMap: { [key: string]: string } = {
  'fr-ht': 'ht',
};

/**
 * Normalizes language codes to handle locale variants across devices
 * @param lng - The language code from browser/device (e.g., 'fr-HT', 'en-US')
 * @returns Normalized language code (e.g., 'ht', 'en')
 */
export const normalizeLanguageCode = (lng: string): string => {
  if (!lng) return 'en'; // Return fallback for falsy inputs

  const lowerLng = lng.toLowerCase();
  return languageMap[lowerLng] || lowerLng; // Return lowercase if no mapping
};

const customLanguageDetector = {
  name: 'customLanguageDetector',
  lookup() {
    const navigatorLang =
      navigator.language ||
      (navigator.languages && navigator.languages.length > 0 ? navigator.languages[0] : undefined);
    if (navigatorLang) {
      const normalized = normalizeLanguageCode(navigatorLang);
      return normalized;
    }
    return undefined;
  },
  cacheUserLanguage() {},
};

i18n
  .use({
    type: 'languageDetector',
    async: false,
    init: () => {},
    detect: () => customLanguageDetector.lookup(),
    cacheUserLanguage: () => {},
  })
  .use(LanguageDetector) // Use the standard language detector as fallback
  .use(initReactI18next) // Passes i18n down to react-i18next
  .init({
    resources,
    fallbackLng: 'en', // Default language if detection fails
    interpolation: {
      escapeValue: false, // React already safes from xss
    },
    detection: {
      // Order and from where user language should be detected
      // customLanguageDetector runs first to normalize navigator.language
      order: [
        'querystring',
        'cookie',
        'localStorage',
        'customLanguageDetector',
        'navigator',
        'htmlTag',
        'path',
        'subdomain',
      ],

      // Keys or params to lookup language from
      lookupQuerystring: 'lng',
      lookupCookie: 'i18next',
      lookupLocalStorage: 'i18nextLng',
      lookupFromPathIndex: 0,
      lookupFromSubdomainIndex: 0,

      // Cache user language on
      caches: [], // Disable caching to ensure dynamic language detection
      excludeCacheFor: ['cimode'], // Languages to not persist (cookie, localStorage)

      // Optional expire and domain for set cookie
      // cookieMinutes: 3,
      // cookieDomain: 'myDomain',

      // Optional htmlTag with lang attribute, the default is:
      htmlTag: document.documentElement,
    },
  })
  .then(() => {
    const detectedLang = i18n.language;
    const normalizedLang = normalizeLanguageCode(detectedLang);

    if (normalizedLang !== detectedLang) {
      i18n.changeLanguage(normalizedLang);
    }
  })
  .catch(() => {
    // Initialization failed - i18n will use fallback language
  });

export default i18n;
